import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
          const userData = JSON.parse(storedUser);
          
          // Verify token is still valid by fetching profile
          try {
            let response;
            let currentUser;
            
            if (userData.role === 'seller') {
              // For sellers, use seller profile endpoint
              response = await api.get('/api/seller/profile');
              currentUser = response.data.data;
            } else {
              // For admin, use auth profile endpoint
              response = await authAPI.getProfile();
              currentUser = response.data.data.user;
            }
            
            setUser(currentUser);
            setIsAuthenticated(true);
            
            // Update stored user data if it's different
            if (JSON.stringify(userData) !== JSON.stringify(currentUser)) {
              localStorage.setItem('user', JSON.stringify(currentUser));
            }
          } catch (profileError) {
            // If profile fetch fails, use stored data but mark as potentially stale
            console.log('Profile fetch failed, using stored data:', profileError);
            setUser(userData);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        // Token is invalid, clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      
      // Try seller login first, then admin login
      let response;
      let userData;
      let token;
      let refreshToken;
      
      try {
        // Try seller login endpoint
        response = await api.post('/api/seller/login', credentials);
        const data = response.data.data;
        userData = data.seller;
        token = data.token;
        refreshToken = data.refreshToken || token; // Use token as refresh if not provided
      } catch (sellerError) {
        // If seller login fails, try admin login
        try {
          response = await authAPI.login(credentials);
          const data = response.data.data;
          userData = data.user;
          token = data.token;
          refreshToken = data.refreshToken;
        } catch (adminError) {
          // Both failed, throw the original seller error
          throw sellerError;
        }
      }

      // Check if user has required role (seller or admin)
      if (!['seller', 'admin'].includes(userData.role)) {
        throw new Error('Access denied. This portal is for sellers and administrators only.');
      }

      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);

      const userName = userData.name || userData.fullName || userData.email;
      toast.success(`Welcome back, ${userName}!`);
      return { success: true, user: userData };

    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      const { user: newUser, token, refreshToken } = response.data.data;

      // Check if user has required role
      if (!['seller', 'admin'].includes(newUser.role)) {
        throw new Error('Registration failed. Invalid role specified.');
      }

      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      setUser(newUser);
      setIsAuthenticated(true);

      const message = newUser.role === 'seller' 
        ? 'Registration successful! Your seller account is pending approval.'
        : 'Registration successful!';
      
      toast.success(message);
      return { success: true, user: newUser };

    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Even if logout API fails, we still want to clear local storage
      console.error('Logout API error:', error);
    } finally {
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      setUser(null);
      setIsAuthenticated(false);
      
      toast.success('Logged out successfully');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      const updatedUser = response.data.data.user;
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully');
      return { success: true, user: updatedUser };
      
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData);
      toast.success('Password changed successfully');
      return { success: true };
      
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await authAPI.uploadAvatar(formData);
      const updatedUser = response.data.data.user;
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Avatar updated successfully');
      return { success: true, user: updatedUser };
      
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to upload avatar';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Check if seller is approved
  const isSellerApproved = () => {
    return user?.role === 'seller' ? user?.isApproved : true;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    uploadAvatar,
    hasRole,
    hasAnyRole,
    isSellerApproved,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
